import { loadSwiper } from './swiper-loader.js';
import { loadFancybox } from './fancybox-loader.js';

const LIGHTBOX_CLICK_THRESHOLD = 10;
const LIGHTBOX_CLICK_SUPPRESS_MS = 300;
const WHEEL_BURST_MS = 160;
const WHEEL_PEAK_DECAY_MS = 220;
const WHEEL_SPIKE_RATIO = 1.8;
const WHEEL_SPIKE_MIN = 14;

if (!customElements.get('product-media')) {
    customElements.define('product-media', class extends HTMLElement {
        #hasDragged = false;
        #pointerStart = null;
        #suppressClickUntil = 0;
        #wheelPeak = 0;
        #wheelPending = 0;
        #wheelEventAt = 0;
        #slidAt = 0;

        constructor() {
            super();

            this.swiper = null;
            this.Fancybox = null;
            this.fancyboxSelector = '[data-fancybox^="gallery-"]';
        }

        connectedCallback() {
            this.initCarousel();
            this.addEventListener('click', this.#handleClick, true);
            this.addEventListener('pointerdown', this.#handlePointerDown);
            this.addEventListener('wheel', this.#handleWheel, { passive: false });
        }

        disconnectedCallback() {
            this.removeEventListener('click', this.#handleClick, true);
            this.removeEventListener('pointerdown', this.#handlePointerDown);
            this.removeEventListener('wheel', this.#handleWheel);
            this.#unbindPointerTracking();
            loadFancybox().then((Fancybox) => this.#destroyLightbox(Fancybox));
        }

        async initCarousel() {
            const elCarousel = this.querySelector('.product-media--carousel');
            const elPrev = this.querySelector('[data-navigation="prev"]');
            const elNext = this.querySelector('[data-navigation="next"]');
            const enabled = this.querySelectorAll('.product-media--slide').length > 1;
            this.swiper && this.swiper.destroy(true, true);
            this.#hasDragged = false;

            const Swiper = await loadSwiper();
            this.swiper = new Swiper(elCarousel, {
                enabled: enabled,
                loop: false,
                slidesPerView: 'auto',
                spaceBetween: 8,
                slidesOffsetBefore: enabled ? 12 : 0,
                slidesOffsetAfter: enabled ? 12 : 0,
                // Trackpad swipes are handled here. Swiper's mousewheel skips ahead on Chrome.
                mousewheel: false,
                ...(enabled && {
                    navigation: {
                        prevEl: elPrev,
                        nextEl: elNext,
                    },
                }),
                a11y: {
                    enabled: true,
                    prevSlideMessage: this.dataset.i18nPreviousSlide || 'Previous slide',
                    nextSlideMessage: this.dataset.i18nNextSlide || 'Next slide',
                },
                on: {
                    sliderFirstMove: () => {
                        this.#markAsDragged();
                    },
                },
                breakpoints: {
                    1024: {
                        slidesOffsetBefore: 0,
                        slidesOffsetAfter: 0,
                    }
                }
            });

            this.setAttribute('data-swiper-ready', '');

            try {
                await this.initLightbox();
            } catch (error) {
                console.error('product-media lightbox init failed', error);
            }
        }

        async initLightbox() {
            if (!this.querySelector(this.fancyboxSelector)) return;

            const Fancybox = await loadFancybox();
            this.Fancybox = Fancybox;
            this.#destroyLightbox(Fancybox);
        }

        setVariantTags({ discount = '', freeShipping = false } = {}) {
            const elTags = this.querySelector('[data-media-tags]') ?? this;
            const elSaleTag = elTags.querySelector('[data-sale-tag]');
            const elShippingTag = elTags.querySelector('[data-shipping-tag]');
            const hasDiscount = Boolean(discount);

            if (elSaleTag) {
                elSaleTag.toggleAttribute('hidden', !hasDiscount);
                elSaleTag.textContent = hasDiscount ? discount : '';
            }

            if (elShippingTag) {
                elShippingTag.toggleAttribute('hidden', !freeShipping);
            }
        }

        setVariantMedia(templateEl) {
            if (!templateEl || !templateEl.content) return;

            const elWrapper = this.querySelector('.swiper-wrapper');
            if (!elWrapper) return;

            this.removeAttribute('data-swiper-ready');

            const fragment = templateEl.content.cloneNode(true);
            const elImages = fragment.querySelectorAll('img');
            let imageIndex = 0;

            for (const elImage of elImages) {
                elImage.classList.add('in-view');

                if (imageIndex === 0) {
                    elImage.setAttribute('loading', 'eager');
                    elImage.setAttribute('fetchpriority', 'high');
                } else {
                    elImage.setAttribute('loading', 'lazy');
                    elImage.setAttribute('fetchpriority', 'low');
                }

                imageIndex += 1;
            }

            elWrapper.replaceChildren(fragment);
            window.initLazyLoad?.();

            this.initCarousel();
        }

        #destroyLightbox(Fancybox) {
            if (!Fancybox) return;

            Fancybox.close(true);
        }

        #handleWheel = (event) => {
            if (!this.swiper || this.swiper.destroyed || !this.swiper.enabled) return;
            if (event.ctrlKey) return;
            if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

            event.preventDefault();

            const now = Date.now();
            const magnitude = Math.abs(event.deltaX);
            const gap = this.#wheelEventAt ? now - this.#wheelEventAt : 0;
            this.#wheelEventAt = now;

            if (gap > 0) {
                this.#wheelPeak *= Math.exp(-gap / WHEEL_PEAK_DECAY_MS);
            }

            const sinceSlide = this.#slidAt ? now - this.#slidAt : Infinity;
            const isNewSwipe = magnitude >= WHEEL_SPIKE_MIN && magnitude > this.#wheelPeak * WHEEL_SPIKE_RATIO;
            this.#wheelPeak = Math.max(this.#wheelPeak, magnitude);

            // The opening burst and the slowing tail are one swipe. Another swipe
            // speeds up again, including while the pointer stays over the image.
            if (!this.#slidAt) {
                this.#wheelPending += event.deltaX;
                if (Math.abs(this.#wheelPending) < WHEEL_SPIKE_MIN) return;
                this.#wheelPending = 0;
            } else if (sinceSlide < WHEEL_BURST_MS || !isNewSwipe) {
                return;
            }

            this.#slidAt = now;

            if (event.deltaX > 0) {
                this.swiper.slideNext();
            } else {
                this.swiper.slidePrev();
            }
        };

        #handlePointerDown = (event) => {
            if (event.target.closest('[data-navigation]')) return;
            if (event.isPrimary === false) return;
            if (event.pointerType === 'mouse' && event.button !== 0) return;

            this.#hasDragged = false;
            this.#pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
            this.#bindPointerTracking();
        };

        #handlePointerMove = (event) => {
            if (event.pointerId !== this.#pointerStart?.id) return;

            const deltaX = event.clientX - this.#pointerStart.x;
            const deltaY = event.clientY - this.#pointerStart.y;

            if (Math.hypot(deltaX, deltaY) >= LIGHTBOX_CLICK_THRESHOLD) {
                this.#markAsDragged();
                this.#unbindPointerTracking();
            }
        };

        #handlePointerUp = (event) => {
            if (event.pointerId !== this.#pointerStart?.id) return;
            this.#unbindPointerTracking();
        };

        #bindPointerTracking() {
            this.#unbindPointerTracking();
            window.addEventListener('pointermove', this.#handlePointerMove, { capture: true, passive: true });
            window.addEventListener('pointerup', this.#handlePointerUp, { capture: true, passive: true });
            window.addEventListener('pointercancel', this.#handlePointerUp, { capture: true, passive: true });
        }

        #unbindPointerTracking() {
            window.removeEventListener('pointermove', this.#handlePointerMove, { capture: true });
            window.removeEventListener('pointerup', this.#handlePointerUp, { capture: true });
            window.removeEventListener('pointercancel', this.#handlePointerUp, { capture: true });
        }

        #markAsDragged() {
            this.#hasDragged = true;
            this.#suppressClickUntil = Date.now() + LIGHTBOX_CLICK_SUPPRESS_MS;
        }

        #isLightboxClickSuppressed() {
            return this.#hasDragged || Date.now() < this.#suppressClickUntil;
        }

        #handleClick = (event) => {
            if (event.target.closest('[data-navigation]')) return;

            const elTrigger = event.target.closest('[data-lightbox-trigger]');
            if (elTrigger && this.contains(elTrigger)) {
                event.preventDefault();
                this.#openLightboxFromSlide(elTrigger.closest('.product-media--slide'));
                return;
            }

            if (this.#isLightboxClickSuppressed()) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            const elMedia = event.target.closest('.product-media--media');
            if (!elMedia || !this.contains(elMedia)) return;

            event.preventDefault();
            event.stopPropagation();
            this.#openLightboxFromSlide(elMedia.closest('.product-media--slide'));
        };

        #openLightboxFromSlide(elSlide) {
            const elSource = elSlide?.querySelector('[data-fancybox]');
            if (!elSource) return;

            if (this.Fancybox) {
                const galleryId = elSource.getAttribute('data-fancybox');
                const elSources = galleryId
                    ? [...this.querySelectorAll(`[data-fancybox="${galleryId}"]`)]
                    : [elSource];
                const startIndex = elSources.indexOf(elSource);

                this.Fancybox.fromNodes(elSources, {
                    startIndex: Math.max(0, startIndex),
                    ...this.#getLightboxOptions(this.dataset.i18nSlideOf || 'Slide __CURRENT__ of __TOTAL__'),
                });
            } else {
                elSource.click();
            }
        }

        #getLightboxOptions(slideLabelTemplate) {
            return {
                Carousel: {
                    formatCaption: () => '',
                },
                on: {
                    'Carousel.ready Carousel.change': (fancybox) => {
                        this.#updateFancyboxA11y(fancybox, slideLabelTemplate);
                    },
                    'Carousel.Panzoom.ready': () => {
                        this.#labelFancyboxToolbar();
                    },
                },
            };
        }

        #updateFancyboxA11y(fancybox, slideLabelTemplate) {
            const slide = fancybox.getSlide();
            const slides = fancybox.getCarousel().getSlides();
            const currentIndex = slide.index;
            const label = slideLabelTemplate
                .replace('__CURRENT__', String(currentIndex + 1))
                .replace('__TOTAL__', String(slides.length));

            setTimeout(() => {
                document.querySelectorAll('.fancybox__container .fancybox__slide').forEach((item, index) => {
                    item.setAttribute('role', 'group');

                    if (index === currentIndex) {
                        item.setAttribute('aria-label', label);
                    }
                });

                const elSlides = this.querySelectorAll('.product-media--slide');
                const elActiveMedia = elSlides[currentIndex]?.querySelector('.product-media--media img');

                document.querySelectorAll('.f-thumbs__viewport .f-thumbs__slide button').forEach((item) => {
                    item.removeAttribute('aria-current');
                    item.setAttribute('tabindex', '0');

                    if (item.parentElement?.classList.contains('is-selected')) {
                        item.setAttribute('aria-current', 'true');
                    }

                    const elThumbImg = item.querySelector('img');
                    if (elThumbImg && elActiveMedia?.alt) {
                        elThumbImg.alt = elActiveMedia.alt;
                    }
                });

                const elCounter = document.querySelector('.f-counter');
                if (elCounter) {
                    elCounter.setAttribute('aria-label', label);
                    elCounter.setAttribute('role', 'status');

                    for (const elSpan of elCounter.querySelectorAll('span')) {
                        elSpan.setAttribute('aria-hidden', 'true');
                    }
                }

                this.#labelFancyboxToolbar();
            }, 100);
        }

        #labelFancyboxToolbar() {
            setTimeout(() => {
                for (const elButton of document.querySelectorAll('.f-carousel__toolbar .f-button[title]')) {
                    const title = elButton.getAttribute('title');
                    if (title) {
                        elButton.setAttribute('aria-label', title);
                    }
                }
            }, 100);
        }
    });
}
